<script>
import SimulatorStudentDetailComponent from '../features/SimulatorStudentDetailComponent';
import SimulatorStudentDetailSectionView from './SimulatorStudentDetailSectionView';
import SimulatorStudentDetailResultView from './SimulatorStudentDetailResultView';


export default {
  name: 'SimulatorStudentDetailView',
  components: {
    'simulator-detail-section-view': SimulatorStudentDetailSectionView,
    'simulator-result-view': SimulatorStudentDetailResultView,
  },
  extends: SimulatorStudentDetailComponent,
  computed: {
    // La barra de retorno respeta la jerarquía: con una sección abierta
    // regresa al detalle del simulador; sin sección, sale al inicio.
    backLabel() {
      return this.sectionSelected ? 'Volver al simulador' : 'Volver al Inicio';
    },
  },
};

</script>
<template>
  <div class="sim_apply_page">
    <!--
      Simulador de prueba a pantalla completa, sin el sidebar del dashboard:
      comparte el mismo diseño que el simulador oficial para que el alumno
      no perciba un cambio de contexto entre ambos modos.
    -->
    <div class="wrapper is-fullpage">
      <div class="sa4d25">
        <div class="container-fluid">
          <div class="section3125">
            <!-- Barra de retorno -->
            <div class="course_back_bar">
              <button
                type="button"
                class="course_back_btn"
                @click="onBack"
              >
                <i class="uil uil-arrow-left" />
                <span>{{ backLabel }}</span>
              </button>
            </div>

            <template v-if="simulator && !isFinished">
              <!-- Encabezado de página -->
              <div class="stream_page_header">
                <div class="stream_header_left">
                  <div class="stream_header_icon">
                    <i class="fas fa-flask" />
                  </div>
                  <div class="stream_header_text">
                    <h4 class="item_title">
                      Simulador de prueba
                    </h4>
                    <p class="stream_header_subtitle">
                      Simulación personal sin costo, generada a partir del nivel de dificultad que elegiste.
                    </p>
                  </div>
                </div>
                <div class="stream_header_right">
                  <span
                    v-if="simulator.isTimer && timerLabel"
                    class="sim_apply_timer_pill"
                  >
                    <i class="uil uil-stopwatch" /> {{ timerLabel }}
                  </span>
                  <span
                    v-else
                    class="stream_live_pill"
                  >
                    <i class="uil uil-clock-three mr-1" />
                    {{ simulator.isTimer ? 'Con cronómetro' : 'Práctica libre' }}
                  </span>
                </div>
              </div>

              <!-- Resumen: portada + ficha del examen -->
              <div
                v-if="!sectionSelected"
                class="sim_apply_summary"
              >
                <div class="sim_apply_summary_media">
                  <div class="sim_apply_cover">
                    <div class="sim_apply_cover_placeholder">
                      <i class="uil uil-flask" />
                      <span>Simulación de práctica</span>
                    </div>
                  </div>
                  <div class="sim_apply_summary_cta">
                    <button
                      v-if="!timerLabel && !isFinished && simulator.isTimer"
                      type="button"
                      class="account_btn_primary"
                      @click="onStartTimer"
                    >
                      <i class="uil uil-play" /> Iniciar simulador
                    </button>
                  </div>
                </div>

                <div class="sim_apply_summary_body">
                  <h4 class="sim_apply_summary_title">
                    Información del simulador
                  </h4>
                  <p class="sim_apply_summary_desc">
                    Esta simulación se armó con preguntas del nivel
                    {{ getDifficultyLabel(simulator.difficultyLevel) }} y no afecta tu puntaje oficial.
                  </p>

                  <div class="sim_apply_facts">
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">Nivel</span>
                      <span class="sim_apply_fact_value">
                        {{ getDifficultyLabel(simulator.difficultyLevel) }}
                      </span>
                    </div>
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">N.º de preguntas</span>
                      <span class="sim_apply_fact_value">
                        {{ simulator.questionQuantity }}
                      </span>
                    </div>
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">Duración</span>
                      <span class="sim_apply_fact_value">
                        {{ simulator.duration }} min
                      </span>
                    </div>
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">Temporizado</span>
                      <span class="sim_apply_fact_value">
                        {{ simulator.isTimer ? 'Sí' : 'No' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Aviso de inicio -->
              <div
                v-if="!isFinished && !sectionSelected"
                class="sim_apply_notice"
              >
                <i class="uil uil-info-circle" />
                <span v-if="!timerLabel">
                  Para comenzar tu simulador, haz clic en «Iniciar simulador».
                </span>
                <span v-else>
                  Haz clic en la sección que desees comenzar a responder.
                </span>
              </div>

              <!-- Secciones disponibles -->
              <div
                v-if="!isFinished && !sectionSelected"
                class="sim_apply_sections"
              >
                <div class="dash_section_header">
                  <div class="dash_section_title">
                    <i class="uil uil-layer-group" /> Secciones
                    <span class="dash_section_count">
                      {{ (simulator.simulatorSections || []).length }}
                    </span>
                  </div>
                </div>

                <div class="sim_apply_section_list">
                  <button
                    v-for="(section, sectionIndex) in (simulator.simulatorSections || [])"
                    :key="sectionIndex"
                    type="button"
                    class="sim_apply_section"
                    @click="onSelectSection(section)"
                  >
                    <span class="sim_apply_section_icon">
                      <i class="uil uil-angle-right" />
                    </span>

                    <span class="sim_apply_section_body">
                      <span class="sim_apply_section_name">
                        {{ section.name }}
                      </span>
                      <span class="sim_apply_section_desc">
                        {{ (section.simulatorQuestions || []).length }} pregunta(s)
                      </span>
                    </span>

                    <span
                      class="sim_apply_section_status"
                      :class="{ is_done: isSectionFinished(section) }"
                    >
                      <i
                        :class="isSectionFinished(section) ? 'uil uil-check-circle' : 'uil uil-clock'"
                      />
                      {{ isSectionFinished(section) ? 'Finalizada' : 'Pendiente' }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- Sección en curso -->
              <div
                v-if="sectionSelected"
                class="sim_apply_active"
              >
                <simulator-detail-section-view
                  :simulator-id="simulator.id"
                  :current-section="sectionSelected"
                />
              </div>

              <!-- Acción de cierre -->
              <div
                v-if="allSectionFinished() && !isFinished && !sectionSelected"
                class="sim_apply_actions"
              >
                <button
                  type="button"
                  class="account_btn_primary"
                  @click="onFinishSimulator"
                >
                  <i class="uil uil-check-circle" /> Finalizar simulador
                </button>
              </div>
            </template>

            <!-- Resultados -->
            <simulator-result-view
              v-if="simulator && isFinished"
              :simulator-id="simulator.id"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-loading.fullscreen.lock="isLoading" />
  </div>
</template>
